"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhereAcrylicIsUsed() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const offerings = [{
    numeral: "I. APPLICATIONS",
    title: "Industrial Signage",
    description: "Precision-cut acrylic solutions designed for high-visibility safety labeling and equipment identification.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/acrylic_industrial_signage.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/acrylic_industrial_signage.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/acrylic_industrial_signage.png-wvc-sizes",
    alt: "Durable industrial acrylic signage mounted on metal equipment",
    href: "/ProductsPage"
  }, {
    numeral: "II. COMPONENTS",
    title: "Custom Fabrications",
    description: "Bespoke acrylic components engineered for electrical enclosures, control panels, and specialized machinery.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/acrylic_custom_fabrication.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/acrylic_custom_fabrication.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/acrylic_custom_fabrication.png-wvc-sizes",
    alt: "Custom fabricated acrylic panel for electrical control systems",
    href: "/PanelBuildersUL508APage"
  }, {
    numeral: "III. MARKING",
    title: "Precision Engraving",
    description: "High-contrast laser engraving on premium acrylic for permanent, legible, and professional-grade marking.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/acrylic_laser_engraving.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/acrylic_laser_engraving.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/acrylic_laser_engraving.png-wvc-sizes",
    alt: "Laser engraving process on a clear acrylic surface",
    href: "/CustomEngravingPage"
  }];
  return <section data-section-id="3273" id="where-acrylic-is-used" className="relative py-24 md:py-32 lg:py-40 overflow-hidden bg-background text-foreground">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        {}
        <div ref={header.ref} className={`text-center max-w-3xl mx-auto transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs md:text-sm tracking-[0.25em] uppercase mb-8 text-primary">
            — 03 / Applications
          </p>
          <h2 className="font-default font-light text-[2.25rem] sm:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-accent">
            Versatile acrylic solutions.
          </h2>
          <p className="mt-8 text-base md:text-[17px] leading-relaxed font-default text-foreground">
            Engineered for durability and clarity across diverse industrial environments.
          </p>
        </div>

        {}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-10">
          {offerings.map((item, i) => {
          return <OfferingCard key={i} index={i} numeral={item.numeral} title={item.title} description={item.description} image={item.image} srcset={item.srcset} sizes={item.sizes} alt={item.alt} href={item.href} showDivider={i < offerings.length - 1} />;
        })}
        </div>
      </div>
    </section>;
}
function OfferingCard({
  index,
  numeral,
  title,
  description,
  image,
  srcset,
  sizes,
  alt,
  href,
  showDivider
}: {
  index: number;
  numeral: string;
  title: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  href: string;
  showDivider: boolean;
}) {
  const reveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div data-index={index} ref={reveal.ref} className={`relative transition-all duration-1000 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 140}ms`
  }}>
      <Link to={href} className="group block transition-transform duration-700 ease-out hover:-translate-y-1.5" aria-label={`Explore ${title}`}>
        {}
        <div className="relative overflow-hidden rounded-lg shadow-md" style={{
        aspectRatio: "5 / 7"
      }}>
          <img src={image} data-wvc-srcset={srcset} data-wvc-sizes={sizes} alt={alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]" />
          <div className="absolute inset-0 opacity-0 transition-opacity duration-700 ease-out group-hover:opacity-100 bg-gradient-to-b from-transparent to-primary/20" />
        </div>

        {}
        <p className="mt-8 font-default text-xs tracking-[0.25em] uppercase text-primary">
          {numeral}
        </p>

        {}
        <h3 className="mt-4 font-default font-light text-[1.625rem] md:text-[1.75rem] leading-tight tracking-tight text-accent">
          {title}
        </h3>

        {}
        <p className="mt-4 text-[15px] leading-relaxed font-default max-w-md text-foreground">
          {description}
        </p>

        {}
        <div className="mt-6 inline-flex items-center gap-2 font-default text-xs tracking-[0.18em] uppercase text-primary">
          <span>View details</span>
          <ArrowRight className="h-4 w-4 transition-transform duration-500 ease-out group-hover:translate-x-1.5" strokeWidth={1.25} />
        </div>
      </Link>

      {}
      {showDivider && <div className="md:hidden mt-10 h-px w-full bg-border" aria-hidden="true" />}
    </div>;
}